import React from 'react';
import { Box, Typography } from '@mui/material';
import { CircleCheck } from 'lucide-react';
import { StatusPill } from '../../../components/ui';
import { sbg } from '../../../theme/sbgTokens';
import { checkpointView } from './helpers';
import type { WeekendShiftCheckpointDetail } from './types';

export interface CheckpointMarkProps {
  phase: 'start' | 'end';
  checkpoint: WeekendShiftCheckpointDetail;
  /** Show the confirmation or attempt time inline ("Start 9:22 AM ET"). */
  showTime?: boolean;
}

/**
 * One check-in checkpoint. Exceptions (missed, offsite, awaiting) are status
 * pills; a confirmed check-in is quiet text with a check; a window that
 * hasn't opened is plain secondary text.
 */
const CheckpointMark: React.FC<CheckpointMarkProps> = ({ phase, checkpoint, showTime = true }) => {
  const view = checkpointView(phase, checkpoint);

  if (view.kind === 'pill') {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
        <StatusPill tone={view.tone}>{view.label}</StatusPill>
        {showTime && view.time && (
          <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
            {view.time}
          </Typography>
        )}
      </Box>
    );
  }

  if (view.kind === 'confirmed') {
    return (
      <Typography
        variant="body2"
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          color: sbg.text.secondary,
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <CircleCheck size={14} aria-hidden="true" />
        {showTime && view.time ? (
          <span>
            {view.label}
            <span className="sbg-sr-only"> confirmed</span>
            {` ${view.time}`}
          </span>
        ) : (
          `${view.label} confirmed`
        )}
      </Typography>
    );
  }

  return (
    <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary, whiteSpace: 'nowrap' }}>
      {view.label}
    </Typography>
  );
};

export default CheckpointMark;
